import { FC, useEffect, useState } from 'react';
import FSMSubscriptionModal from './components/FSMSubscriptionModal';
import { selectCurrentTeam, selectIsOwner, selectMerchantCustomizations } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FSMPlan } from 'containers/integrations/fsm-integration/constants';
import FSMNotAvaialbleModal from './components/FSMNotAvaialbleModal';
import FSMPlanComparisonModal from './components/FSMPlanComparisonModal';
import { usePostSubscriptionActivityMutation } from 'containers/app/api';
import { FSM_PLANS } from './constants';
import UpgradeFSMModal from './components/UpgradeFSMModal';
import SubscribedModal from './components/SubscribedModal';
import routesPath from 'routes/RoutesPath';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldShowFSMModal } from './utils';
import { FSMSubscriptionIcon } from 'assets/icons';
import { resetCreateJobState } from 'containers/jobs/create-edit-job/createJobSlice';
import { useConfirmSubscriptionMutation } from 'containers/integrations/fsm-integration/api';
import FSMSubscriptionEndedModal from './components/FSMSubscriptionEndedModal';
import { setShowFSMModal } from './modalSlice';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';

interface GlobalModalRendererProps {
  isFullScreenLayout: boolean;
}

const GlobalModalRenderer: FC<GlobalModalRendererProps> = ({ isFullScreenLayout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { subscriptionActivity } = useAppSelector(selectMerchantCustomizations);
  const { currentFSMPlan } = subscriptionActivity ?? {};
  const isOwner = useAppSelector(selectIsOwner);
  const { id: teamId } = useAppSelector(selectCurrentTeam);
  const dispatch = useAppDispatch();
  const showSubscriptionModal = shouldShowFSMModal(location.pathname) ?? false;

  const { isSystemDowngraded, hasDowngradedAfterTrial, isTrialEnded, isUserCancelled } = useFSMPlanInfo();

  const [postSubscriptionActivity, { isLoading: isPostSubscriptionActivityLoading }] =
    usePostSubscriptionActivityMutation();
  const [confirmFSMSubscription, { isLoading: isConfirmSubscriptionLoading }] = useConfirmSubscriptionMutation();

  const [showModal, setShowModal] = useState(true);
  const [showFSMComparePlansModal, setShowFSMComparePlansModal] = useState(false);
  const [showSubscribedModal, setShowSubscribedModal] = useState(false);

  const isDowngraded = isSystemDowngraded || hasDowngradedAfterTrial;

  useEffect(() => {
    setShowModal(true);
  }, [currentFSMPlan]);

  const handleChoosePlanClick = () => {
    setShowFSMComparePlansModal(true);
  };

  const handleCloseFSMComparePlansModal = () => {
    setShowFSMComparePlansModal(false);
  };

  const handleStartTrial = async () => {
    await confirmFSMSubscription({ teamId, body: { hasTrial: true } });
  };

  const handleChoosePlan = async (plan: string) => {
    if (plan === FSM_PLANS.FIELD_SERVE_PLUS) {
      handleStartTrial();
    } else {
      await postSubscriptionActivity({
        teamId,
        payload: {
          feature: 'FSM',
          webFreePlanSelected: plan === FSM_PLANS.FIELD_SERVE,
          webPaidPlanSelected: plan === FSM_PLANS.FIELD_SERVE_PLUS
        }
      });
      handleCloseFSMComparePlansModal();
      if (currentFSMPlan === FSMPlan.NO_PLAN) {
        setShowModal(false);
        setShowSubscribedModal(true);
      }
    }
  };

  const handleCreateJobClick = () => {
    dispatch(resetCreateJobState());
    navigate(routesPath.CREATE_JOB);
    handleCloseSubscribedModal();
  };

  const handleCloseSubscribedModal = () => {
    setShowSubscribedModal(false);
  };

  const goToPreviousPage = () => {
    navigate(-1);
  };

  const handleCloseSubscriptionEndedModal = () => {
    dispatch(setShowFSMModal(false));
  };

  const handleRenewSubscription = () => {
    if (isOwner) {
      navigate(routesPath.FIELD_SERVICE_MANAGEMENT);
    } else {
      handleCloseSubscriptionEndedModal();
    }
  };

  if (showSubscriptionModal || showSubscribedModal) {
    if (isOwner) {
      return (
        <>
          {currentFSMPlan === FSMPlan.NO_PLAN && showModal && (
            <FSMSubscriptionModal
              onChoosePlan={handleChoosePlanClick}
              showCloseButton={isFullScreenLayout}
              handleClose={goToPreviousPage}
            />
          )}
          {currentFSMPlan === FSMPlan.FREE && !isDowngraded && showModal && (
            <UpgradeFSMModal
              onComparePlans={handleChoosePlanClick}
              onStartTrial={handleStartTrial}
              isLoading={isConfirmSubscriptionLoading}
            />
          )}
          {showFSMComparePlansModal && showModal && (
            <FSMPlanComparisonModal
              handleClose={handleCloseFSMComparePlansModal}
              onChoosePlan={handleChoosePlan}
              isLoading={isPostSubscriptionActivityLoading || isConfirmSubscriptionLoading}
              currentFSMPlan={currentFSMPlan}
            />
          )}
          {showSubscribedModal && (
            <SubscribedModal
              title="You're all set!"
              description={
                <div className="text-sbase font-medium text-primaryText">
                  Field Serve is now active for your business.
                  <div className="mt-5"> Let's create your first job.</div>
                </div>
              }
              primaryButtonText="Create new job"
              secondaryButtonText="Done"
              primaryButtonClick={handleCreateJobClick}
              onClose={handleCloseSubscribedModal}
              icon={<FSMSubscriptionIcon />}
            />
          )}
          {isDowngraded && showModal && (
            <FSMSubscriptionEndedModal
              handleClose={handleCloseSubscriptionEndedModal}
              onRenew={handleRenewSubscription}
              showCloseButton={false}
              isFullScreenModal={false}
            />
          )}
        </>
      );
    }
    if (isUserCancelled || isTrialEnded) {
      return (
        <FSMSubscriptionEndedModal
          handleClose={handleCloseSubscriptionEndedModal}
          onRenew={handleRenewSubscription}
          showCloseButton={false}
          isFullScreenModal={false}
        />
      );
    }
    return <FSMNotAvaialbleModal showCloseButton={isFullScreenLayout} handleClose={goToPreviousPage} />;
  }
  return null;
};

export default GlobalModalRenderer;
